import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import type { FuelType } from '../../../../common/enums/fuel-type';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';

export type FleetFuelTankCapacityFormGroup = ReturnType<
  typeof FleetFuelTankCapacityFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-fuel-tank-capacity-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxFormGroupComponent,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
  ],
  templateUrl: './fleet-fuel-tank-capacity-form.component.html',
  styleUrl: './fleet-fuel-tank-capacity-form.component.scss',
})
export class FleetFuelTankCapacityFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<FleetFuelTankCapacityFormGroup>();
  public readonly validation = input.required<ValidationGroup>();

  protected readonly I18N = 'fleet.fleetForm.fuelTankCapacity';

  protected fuelTypes = toSignal(
    this.dictionariesService.getDictionary(DictionaryCode.FUEL_TYPE),
    { initialValue: null },
  );
  protected fuelType = computed(() => {
    const fuelTypes = this.fuelTypes();
    if (!fuelTypes) return null;

    return (
      fuelTypes.find((fuelType) => fuelType.value === this.fuelTypeValue()) ??
      null
    );
  });

  private fuelTypeValue!: Signal<FuelType | null>;

  public ngOnInit(): void {
    const fuelTypeControl = this.form().controls.fuelType;
    this.fuelTypeValue = toSignal(fuelTypeControl.valueChanges, {
      initialValue: fuelTypeControl.value,
      injector: this.injector,
    });

    effect(
      () => {
        const controls = this.form().controls;
        const fields = this.validation().fields;
        const fuelType = this.fuelType();

        setTimeout(() => {
          ValidationHelper.updateControl(controls.mainTank, fields['mainTank']);
          ValidationHelper.updateControl(controls.fuelType, fields['fuelType']);
          ValidationHelper.updateControl(
            controls.adBlueTank,
            fields['adBlueTank'],
          );
          ValidationHelper.updateControl(
            controls.generatorFuelTank,
            fields['generatorFuelTank'],
          );

          ValidationHelper.updateControl(
            controls.additionalTank,
            fields['additionalTank'],
          );
          if (controls.additionalTank.enabled) {
            fuelType?.requireAdditionalTank
              ? controls.additionalTank.addValidators([Validators.required])
              : controls.additionalTank.removeValidators([Validators.required]);
            controls.additionalTank.updateValueAndValidity();
          }
        });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferenceService: PreferencesService,
  ) {
    return fb.group({
      mainTank: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      fuelType: fb.control<FuelType | null>(
        preferenceService.defaultFuelType(),
      ),
      additionalTank: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      adBlueTank: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      generatorFuelTank: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
    });
  }
}
